import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Avatar,
} from "@mui/material";
import { Add as AddIcon, ArrowBack as BackIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { useDebounce } from "../hooks/useDebounce";
import { fetchSubcategoriesListing } from "../store/slices/categoriesSlice";
import SubcategoryDialog from "../components/dialogs/SubcategoryDialog";
import DeleteConfirmDialog from "../components/dialogs/DeleteConfirmDialog";
import EnhancedTable from "../components/common/EnhancedTable";
import { categoryAPI } from "../services/api";
import StatusChip from "../utils/StatusChip";
import ExportImportButtons from "../components/common/ExportImportButtons";

interface CategoryMedia {
  id: string;
  categoryname: string;
  createdby: string;
  modifiedby: string | null;
  masterdetailId: string;
  categoryId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
}

interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
  categorymedia: CategoryMedia[];
}

interface Subcategory {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
  categorymedia: CategoryMedia[];
}

interface SubcategoryFormData {
  id?: string;
  name: string;
  status: string;
  image?: File | string;
}

const SubcategoriesList: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  // State for API data
  const [categoryData, setCategoryData] = useState<Category | null>(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();

  const dispatch = useAppDispatch();

  // Redux state for subcategories (destructured like categories)
  const { subcategories, subcategoriesLoading, subcategoriesPagination } =
    useAppSelector((state: any) => state.categories);

  const [columnFilters, setColumnFilters] = useState({
    name: "",
    categorystatus: "all",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // New states for subcategory dialog
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<SubcategoryFormData | null>(null);
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | "view">("add");

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] =
    useState<Subcategory | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [columns, setColumns] = useState([
    {
      id: "name",
      label: "Subcategory Name",
      minWidth: 200,
      sortable: true,
      draggable: true,
      format: (value: string, row: Subcategory) => {
        const imageUrl = row.categorymedia?.[0]?.categoryname
          ? categoryAPI.getMediaDownloadUrl(row.categorymedia[0].categoryname)
          : undefined;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={imageUrl}
              alt={value}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
                fontSize: "1rem",
                fontWeight: "bold",
                border: "2px solid",
                borderColor: "primary.light",
              }}
            >
              {value.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "text.secondary" }}
            >
              {value}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "totalproducts",
      label: "Number of Products",
      minWidth: 160,
      align: "center" as const,
      draggable: true,
      sortable: true,
      format: (value: number) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {value || 0}
        </Typography>
      ),
    },
    {
      id: "categorystatus",
      label: "Status",
      minWidth: 120,
      align: "center" as const,
      draggable: true,
      format: (value: number) => <StatusChip status={value} />,
    },
  ]);

  // Ref to track if column order has been initialized
  const isInitialized = React.useRef(false);

  const debouncedCategoryName = useDebounce(columnFilters.name, 500);

  // Handle column reordering
  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    // Optionally save to localStorage for persistence
    localStorage.setItem(
      "subcategoriesTableColumnOrder",
      JSON.stringify(newColumns.map((col: any) => col.id))
    );
  };

  // Load saved column order on component mount
  React.useEffect(() => {
    // Only run once on mount
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedColumnOrder = localStorage.getItem(
      "subcategoriesTableColumnOrder"
    );
    if (savedColumnOrder) {
      try {
        const savedIds = JSON.parse(savedColumnOrder);
        const reorderedColumns = savedIds
          .map((id: string) => columns.find((col) => col.id === id))
          .filter(Boolean);

        // Add any new columns that weren't in the saved order
        const existingIds = reorderedColumns.map((col: any) => col.id);
        const newColumns = columns.filter(
          (col: any) => !existingIds.includes(col.id)
        );

        setColumns([...reorderedColumns, ...newColumns]);
      } catch (error) {
        console.error("Error loading saved column order:", error);
      }
    }
  }, [columns]); // Now safe to include 'columns' in dependency array

  const loadSubcategories = useCallback(() => {
    const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
      "where[parentId]": categoryId,
    };

    if (debouncedCategoryName.trim()) {
      params["search"] = debouncedCategoryName.trim();
    }

    if (
      columnFilters.categorystatus &&
      columnFilters.categorystatus !== "all"
    ) {
      params["where[categorystatus]"] =
        columnFilters.categorystatus === "active" ? 1 : 0;
    }

    if (sortField) {
      params.sort = `${sortField} ${sortDirection}`;
    }

    dispatch(fetchSubcategoriesListing(params));
  }, [
    dispatch,
    currentPage,
    itemsPerPage,
    sortField,
    sortDirection,
    categoryId,
    columnFilters,
    debouncedCategoryName,
  ]);

  useEffect(() => {
    const fetchCategory = async () => {
      if (!categoryId) return;
      setCategoryLoading(true);
      try {
        const response = await categoryAPI.getCategoryDetails(categoryId);
        setCategoryData(response.data);
      } catch (error) {
        setCategoryData(null);
        toast.error("Failed to load category data");
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategory();
  }, [categoryId]);

  useEffect(() => {
    if (currentPage > 0 && itemsPerPage > 0) {
      const params: any = {
        page: currentPage,
        items_per_page: itemsPerPage,
        "where[parentId]": categoryId,
      };

      if (debouncedCategoryName.trim()) {
        params["search"] = debouncedCategoryName.trim();
      }

      if (
        columnFilters.categorystatus &&
        columnFilters.categorystatus !== "all"
      ) {
        params["where[categorystatus]"] =
          columnFilters.categorystatus === "active" ? 1 : 0;
      }

      if (sortField) {
        params.sort = `${sortField} ${sortDirection}`;
      }

      dispatch(fetchSubcategoriesListing(params));
    }
  }, [
    dispatch,
    debouncedCategoryName,
    columnFilters.categorystatus,
    currentPage,
    itemsPerPage,
    sortField,
    sortDirection,
    categoryId,
  ]);

  const handleColumnFilterChange = (columnId: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as the sort field with ascending direction
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleAddSubcategory = () => {
    setSelectedSubcategory(null);
    setDialogMode("add");
    setSubcategoryDialogOpen(true);
  };

  const handleEditSubcategory = (subcategory: Subcategory) => {
    setSelectedSubcategory({
      id: subcategory.id,
      name: subcategory.name,
      status: subcategory.categorystatus === 1 ? "1" : "0",
      image: subcategory.categorymedia?.[0]?.categoryname || "",
    });
    setDialogMode("edit");
    setSubcategoryDialogOpen(true);
  };

  const handleViewProducts = (subcategory: Subcategory) => {
    navigate(`/subcategories/${categoryId}/products/${subcategory.id}`);
  };

  const handleSaveSubcategory = async (
    subcategoryData: SubcategoryFormData
  ) => {
    try {
      // The SubcategoryDialog already handles the API calls, so we just need to refresh the data
      setSubcategoryDialogOpen(false);
      setSelectedSubcategory(null);
      setDialogMode("add");
      // Refresh data
      loadSubcategories();
    } catch (error) {
      toast.error("Failed to save subcategory.");
    }
  };

  const handleDeleteSubcategory = (subcategory: Subcategory) => {
    setSubcategoryToDelete(subcategory);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!subcategoryToDelete) return;

    setDeleteLoading(true);
    try {
      // Call the actual delete API
      await categoryAPI.deleteSubcategory(subcategoryToDelete.id);
      toast.success("Subcategory deleted successfully!");
      setDeleteDialogOpen(false);
      setSubcategoryToDelete(null);
      loadSubcategories(); // Reload subcategories to show updated list
    } catch (error) {
      toast.error("Failed to delete subcategory");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSubcategoryToDelete(null);
  };

  const handleBackToCategories = () => {
    navigate("/categories");
  };

  const handleExportSubcategories = async () => {
    if (!Array.isArray(subcategories) || subcategories.length === 0) {
      toast.info("There is no subcategory data to export");
      return;
    }
    try {
      const response = await categoryAPI.exportSubCategories(
        categoryData?.id || ""
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "subcategories_export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exported subcategory data successfully");
    } catch (error) {
      toast.error("Failed to export subcategories");
    }
  };

  const handleImportSubcategories = () => {
    /* TODO: implement import functionality */
  };

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      {subcategoriesLoading || categoryLoading ? null : (
        <>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body1"
              onClick={handleBackToCategories}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <BackIcon sx={{ mr: 0.5 }} /> Categories
            </Link>
            <Typography color="text.primary">{categoryData?.name}</Typography>
          </Breadcrumbs>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              p: 2,
              borderRadius: 3,
              background: "#fff",
              boxShadow: "0 4px 18px -4px rgba(0,0,0,0.12)",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
                Subcategories{" "}
                {categoryData?.name ? `- ${categoryData?.name}` : ""}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Manage subcategories, view details and export data.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
              <ExportImportButtons
                onExport={handleExportSubcategories}
                onImport={handleImportSubcategories}
              />
              <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleAddSubcategory}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: "0 4px 12px -2px rgba(0,0,0,0.18)",
                  "&:hover": { boxShadow: "0 6px 16px -2px rgba(0,0,0,0.25)" },
                }}
              >
                Add Subcategory
              </Button>
            </Box>
          </Box>

          <EnhancedTable
            columns={columns}
            data={subcategories || []}
            loading={subcategoriesLoading || categoryLoading}
            onEdit={handleEditSubcategory}
            onDelete={handleDeleteSubcategory}
            onView={handleViewProducts}
            showActions={true}
            emptyMessage="No subcategories data found"
            useExternalPagination={true}
            pagination={subcategoriesPagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            useExternalSorting={true}
            onSort={handleSort}
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            enableColumnReordering={true}
            onColumnReorder={handleColumnReorder}
            showStatusChangeCondition={(row: Category) =>
              row.categorystatus === 1 || row.categorystatus === 0
            }
            columnFilters={columnFilters}
            onColumnFilterChange={handleColumnFilterChange}
            onSelectionChange={() => {}}
            showSelection={false}
            enableStickyFilters
            entityName="subcategory"
          />
        </>
      )}

      {/* Subcategory Dialog */}
      <SubcategoryDialog
        open={subcategoryDialogOpen}
        onClose={() => setSubcategoryDialogOpen(false)}
        subcategory={selectedSubcategory}
        onSave={handleSaveSubcategory}
        mode={dialogMode}
        categoryName={categoryData?.name || ""}
        parentId={categoryId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Subcategory"
        message="Are you sure you want to delete this subcategory? All its products will also be deleted."
        itemName={subcategoryToDelete?.name}
        itemType="subcategory"
        loading={deleteLoading}
      />
    </Box>
  );
};

export default SubcategoriesList;
